/*
 * File: app.routes.ts
 * Project: rast-app
 * Create Date: Tuesday, 15th July 2025
 * Author: Pedro Santos (pedro.santos@vonbraunlabs.com)
 * -----
 * Last Modified: Wednesday, 13th May 2026 12:38:16 pm
 * Modified by: Guilherme Oliveira (guilherme.oliveira@vonbraunlabs.com)
 * -----
 * Copyright (c) 2026 CPA Wernher von Braun
 * ----
 * HISTORY:
 * Date      	By	Comments
 * ----------	---	---------------------------------------------------------
 */

import { Routes } from '@angular/router';
import { AuthGuard } from '../core/guards/auth/auth.guard';
import { notAuthGuard } from '../core/guards/not-auth/not-auth.guard';
import { validateTokenGuard } from '../core/guards/validate-token/validate-token-guard';
import { resetPasswordGuard } from '../core/guards/reset-password/reset-password-guard';

export const routes: Routes = [
  {
    path: 'auth',
    canActivate: [notAuthGuard],
    loadComponent: () =>
      import('../features/authentication/authentication/authentication.component').then(
        (m) => m.AuthenticationComponent,
      ),
    children: [
      {
        path: 'login',
        loadComponent: () =>
          import('../features/authentication/login/login.component').then(
            (m) => m.LoginComponent,
          ),
      },
      {
        path: 'signup',
        loadComponent: () =>
          import('../features/authentication/sign-up/sign-up.component').then(
            (m) => m.SignUpComponent,
          ),
      },
      {
        path: 'forgot-password',
        loadComponent: () =>
          import('../features/authentication/forgot-password/forgot-password.component').then(
            (m) => m.ForgotPasswordComponent,
          ),
      },
      {
        path: 'validate-reset-token',
        canActivate: [validateTokenGuard],
        loadComponent: () =>
          import('../features/authentication/validate-reset-token/validate-reset-token.component').then(
            (m) => m.ValidateResetTokenComponent,
          ),
      },
      {
        path: 'reset-password',
        canActivate: [resetPasswordGuard],
        loadComponent: () =>
          import('../features/authentication/reset-password/reset-password.component').then(
            (m) => m.ResetPasswordComponent,
          ),
      },
    ],
  },
  {
    path: 'tabs',
    canActivate: [AuthGuard],
    loadComponent: () =>
      import('./tabs/tabs.component').then((m) => m.TabsComponent),
    children: [
      {
        path: 'home',
        loadComponent: () =>
          import('../features/home/home.component').then(
            (m) => m.HomeComponent,
          ),
      },
      {
        path: 'partners',
        loadComponent: () =>
          import('../features/partners/partners.component').then(
            (m) => m.PartnersComponent,
          ),
      },
      {
        path: 'settings',
        loadComponent: () =>
          import('../features/profile/profile.component').then(
            (m) => m.ProfileComponent,
          ),
      },
      {
        path: '',
        redirectTo: 'home',
        pathMatch: 'full',
      },
    ],
  },
  {
    path: 'vehicle-register',
    canActivate: [AuthGuard],
    loadComponent: () =>
      import('../features/vehicle-register/vehicle-register/vehicle-register.component').then(
        (m) => m.VehicleRegisterComponent,
      ),
  },
  {
    path: 'vehicle-success',
    canActivate: [AuthGuard],
    loadComponent: () =>
      import('../features/vehicle-register/success-register/success-register.component').then(
        (m) => m.SuccessRegisterComponent,
      ),
  },
  {
    path: 'offers',
    canActivate: [AuthGuard],
    loadComponent: () =>
      import('../features/partners/partners.component').then(
        (m) => m.PartnersComponent,
      ),
  },
  {
    path: 'profile/edit',
    canActivate: [AuthGuard],
    loadComponent: () =>
      import('../features/profile/edit-base-information/edit-base-information.component').then(
        (m) => m.EditBaseInformationComponent,
      ),
  },
  {
    path: 'profile/access',
    canActivate: [AuthGuard],
    loadComponent: () =>
      import('../features/profile/edit-access/edit-access.component').then(
        (m) => m.EditAccessComponent,
      ),
  },
  {
    path: 'camera-scanner',
    canActivate: [AuthGuard],
    loadComponent: () =>
      import('../features/camera-scanner/camera-scanner.component').then(
        (m) => m.CameraScannerComponent,
      ),
  },
  {
    path: '',
    redirectTo: '/auth/login',
    pathMatch: 'full',
  },
  {
    path: '**',
    redirectTo: '/auth/login',
  },
];
